import React, { useState, useRef } from 'react';
import { Form, Button, Card, Slider, Input, Select, Tooltip } from '@douyinfe/semi-ui';
import { IconInfoCircle } from '@douyinfe/semi-icons';
import { useTranslation } from 'react-i18next';
import { getValidators, validateField } from '../../hooks/useChatForm';
import { logger } from '@/utils/logger';
import { DynamicNormalFormProps } from './types';

const NormalFormUI: React.FC<DynamicNormalFormProps> = (props) => {
  const { t } = useTranslation();
  const [sliderValues, setSliderValues] = React.useState<Record<string, number>>({});
  const [customInputMode, setCustomInputMode] = useState<Record<string, boolean>>({});
  const [customInputValue, setCustomInputValue] = useState<Record<string, string>>({});
  const customInputRefs = useRef<Record<string, any>>({});
  const formRef = useRef<any>(null);
  const [localOptions, setLocalOptions] = useState<Record<string, { label: string, value: any }[]>>({});
  const [pendingSelectValue, setPendingSelectValue] = useState<Record<string, any>>({});
  const [selectValue, setSelectValue] = useState<Record<string, any>>({});

  React.useEffect(() => {
    const init: Record<string, { label: string, value: any }[]> = {};
    props.form.fields.forEach(field => {
      if (field.type === 'select' && field.custom === true) {
        let opts = Array.isArray(field.options)
          ? field.options.map(opt => ({ label: t(opt.label) || String(opt.label), value: opt.value }))
          : [];
        const v = field.selectedValue !== undefined ? field.selectedValue : field.defaultValue;
        if (opts.length === 0 && v !== undefined && v !== null && v !== '') {
          opts = [{ label: String(v), value: v }];
        }
        init[field.id] = opts;
      }
    });
    setLocalOptions(init);
  }, [props.form.fields]);

  React.useEffect(() => {
    Object.entries(pendingSelectValue).forEach(([fieldId, v]) => {
      if (v !== undefined && v !== null && localOptions[fieldId]) {
        const exists = localOptions[fieldId].some(opt => opt.value === v);
        const current = formRef.current?.getValue ? formRef.current.getValue(fieldId) : undefined;
        if (exists && current !== v && formRef.current?.setValue) {
          formRef.current.setValue(fieldId, v);
          setPendingSelectValue(prev => ({ ...prev, [fieldId]: undefined }));
        }
      }
    });
  }, [localOptions, pendingSelectValue, formRef]);

  React.useEffect(() => {
    const init: Record<string, any> = {};
    props.form.fields.forEach(field => {
      if (field.type === 'select' && field.custom === true) {
        const v = field.selectedValue !== undefined ? field.selectedValue : field.defaultValue;
        if (v !== undefined && v !== null && v !== '') {
          init[field.id] = v;
        }
      }
    });
    setSelectValue(init);
  }, [props.form.fields]);

  const initialValues: Record<string, any> = {};
  props.form.fields.forEach(field => {
    let v = field.selectedValue !== undefined ? field.selectedValue : field.defaultValue;
    if (field.type === 'checkbox') {
      initialValues[field.id] = Array.isArray(v) ? v : v !== undefined && v !== null && v !== '' ? [v] : [];
    } else if (field.type === 'slider') {
      if (v !== undefined && v !== null && v !== '') {
        initialValues[field.id] = Number(v);
      } else {
        initialValues[field.id] = field.min !== undefined ? field.min : 0;
      }
    } else {
      initialValues[field.id] = v !== undefined ? v : '';
    }
  });

  const getFieldRules = (field: any) => {
    const rules: Array<{ validator: (rule: any, value: any) => boolean | Error | Error[]; message: string }> = [];
    if (field.required) {
      rules.push({
        validator: (rule: any, value: any) => {
          const result = validateField(field, value, t);
          return result === true ? true : new Error(result);
        },
        message: t('pages.chat.formRequired', { label: field.label })
      });
    } else if (field.validator) {
      rules.push({
        validator: (rule: any, value: any) => {
          const result = validateField(field, value, t);
          return result === true ? true : new Error(result);
        },
        message: t('pages.chat.formValidate', { label: field.label })
      });
    }
    return rules;
  };

  const handleDoubleClick = (fieldId: string, currentValue?: string) => {
    const field = props.form.fields.find(f => f.id === fieldId);
    if (field && field.custom === true) {
      setCustomInputMode(prev => ({ ...prev, [fieldId]: true }));
      setCustomInputValue(prev => ({ ...prev, [field.id]: currentValue || '' }));
      setTimeout(() => {
        if (customInputRefs.current[fieldId]) {
          customInputRefs.current[fieldId].focus();
        }
      }, 50);
    }
  };
  const handleCustomInputFinish = (fieldId: string) => {
    const value = customInputValue[fieldId]?.trim();
    if (value && formRef.current?.setValue) {
      formRef.current.setValue(fieldId, value);
    }
    setCustomInputMode(prev => ({ ...prev, [fieldId]: false }));
  };

  const handleSubmit: (values: Record<string, any>) => void = (values) => {
    const processedForm = {
      ...props.form,
      fields: props.form.fields.map(field => ({
        ...field,
        selectedValue: values[field.id],
        options: field.type === 'select' && field.custom === true
          ? (localOptions[field.id] || [])
          : field.options
      }))
    };
    props.onFormSubmit?.(props.form.id, values, props.chatId, props.messageId, processedForm);
  };

  const renderField = (field: any) => {
    const label = t(field.label) || field.label;
    const placeholder = field.placeholder ? t(field.placeholder) : '';
    const required = field.required;
    const rules = getFieldRules(field);
    const value = formRef.current?.getValue ? formRef.current.getValue(field.id) : initialValues[field.id];
    const isCustom = field.custom === true;
    const options = isCustom
      ? (localOptions[field.id] || [])
      : (Array.isArray(field.options)
          ? field.options.map((opt: { label: string; value: string | number }) => ({ label: t(opt.label) || String(opt.label), value: opt.value }))
          : []);
    const labelNode = (
      <label className="semi-form-field-label">
        {required && <span className="semi-form-field-label-asterisk">*</span>}
        {label}
        {field.tooltip && (
          <Tooltip content={t(field.tooltip)}>
            <IconInfoCircle style={{ marginLeft: 4, color: 'var(--semi-color-primary)', verticalAlign: 'middle', cursor: 'pointer' }} />
          </Tooltip>
        )}
      </label>
    );
    if (field.type === 'group' && Array.isArray(field.fields)) {
      return (
        <Card key={field.id} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
          {field.fields.map(renderField)}
        </Card>
      );
    }
    switch (field.type) {
      case 'text':
        return <Form.Input 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                placeholder={placeholder} 
                required={required} 
                rules={rules} />;
      case 'textarea':
        return <Form.TextArea 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                placeholder={placeholder} 
                required={required} 
                rules={rules} />;
      case 'number':
        return <Form.Input 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                type="number" 
                placeholder={placeholder} 
                required={required} 
                rules={rules} />;
      case 'select': {
        if (isCustom) {
          let errorMsg = '';
          if (formRef.current && formRef.current.getFieldError) {
            const err = formRef.current.getFieldError(field.id);
            if (Array.isArray(err) && err.length > 0) errorMsg = err[0];
            else if (typeof err === 'string') errorMsg = err;
          }
          const helpText = field.helpText ? t(field.helpText) : '';
          if (customInputMode[field.id]) {
            return (
              <div key={field.id} className="semi-form-field">
                <label className="semi-form-field-label">
                  {required && <span className="semi-form-field-label-asterisk">*</span>}
                  {label}
                  {field.tooltip && (
                    <Tooltip content={t(field.tooltip)}>
                      <IconInfoCircle style={{ marginLeft: 4, color: 'var(--semi-color-primary)', verticalAlign: 'middle', cursor: 'pointer' }} />
                    </Tooltip>
                  )}
                </label>
                <div className="semi-form-field-control">
                  <Input
                    ref={el => customInputRefs.current[field.id] = el}
                    value={customInputValue[field.id] || ''}
                    placeholder={placeholder || t('pages.chat.customInputPlaceholder')}
                    onChange={v => setCustomInputValue(prev => ({ ...prev, [field.id]: v }))}
                    onBlur={() => {
                      const v = customInputValue[field.id]?.trim();
                      if (v) {
                        const exists = (localOptions[field.id] || []).some(opt => opt.value === v);
                        if (!exists) {
                          setLocalOptions(prev => {
                            const updated = {
                              ...prev,
                              [field.id]: [...(prev[field.id] || []), { label: v, value: v }]
                            };
                            setTimeout(() => {
                              setSelectValue(sv => ({ ...sv, [field.id]: v }));
                              if (formRef.current?.setValue) {
                                formRef.current.setValue(field.id, v);
                              }
                            }, 0);
                            return updated;
                          });
                        } else {
                          setSelectValue(sv => ({ ...sv, [field.id]: v }));
                          if (formRef.current?.setValue) {
                            formRef.current.setValue(field.id, v);
                          }
                        }
                        handleCustomInputFinish(field.id);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const v = customInputValue[field.id]?.trim();
                        if (v) {
                          const exists = (localOptions[field.id] || []).some(opt => opt.value === v);
                          if (!exists) {
                            setLocalOptions(prev => {
                              const updated = {
                                ...prev,
                                [field.id]: [...(prev[field.id] || []), { label: v, value: v }]
                              };
                              setTimeout(() => {
                                setSelectValue(sv => ({ ...sv, [field.id]: v }));
                                if (formRef.current?.setValue) {
                                  formRef.current.setValue(field.id, v);
                                }
                              }, 0);
                              return updated;
                            });
                          } else {
                            setSelectValue(sv => ({ ...sv, [field.id]: v }));
                            if (formRef.current?.setValue) {
                              formRef.current.setValue(field.id, v);
                            }
                          }
                        }
                        handleCustomInputFinish(field.id);
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
                {helpText && <div className="semi-form-field-extra">{helpText}</div>}
                {errorMsg && <div className="semi-form-field-error-message">{errorMsg}</div>}
              </div>
            );
          }
          return (
            <div key={field.id} className="semi-form-field">
              <label className="semi-form-field-label">
                {required && <span className="semi-form-field-label-asterisk">*</span>}
                {label}
                {field.tooltip && (
                  <Tooltip content={t(field.tooltip)}>
                    <IconInfoCircle style={{ marginLeft: 4, color: 'var(--semi-color-primary)', verticalAlign: 'middle', cursor: 'pointer' }} />
                  </Tooltip>
                )}
              </label>
              <div className="semi-form-field-control">
                <Tooltip content={t('pages.chat.doubleClickToEdit')} position="right">
                  <Select
                    value={selectValue[field.id]}
                    onChange={v => {
                      setSelectValue(sv => {
                        if (formRef.current?.setValue) {
                          formRef.current.setValue(field.id, v);
                        }
                        return { ...sv, [field.id]: v };
                      });
                    }}
                    optionList={options}
                    placeholder={placeholder}
                    renderSelectedItem={(optionNode: any) => {
                      const isInOptions = options.some((opt: { label: string; value: string | number }) => opt.value === optionNode.value);
                      if (!isInOptions && optionNode.value) {
                        return (
                          <div
                            style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}
                            onDoubleClick={() => handleDoubleClick(field.id, optionNode.value)}
                          >
                            <span style={{ flex: 1 }}>{optionNode.label || optionNode.value}</span>
                            <i className="semi-icon-edit" style={{ fontSize: '12px', marginLeft: '4px' }}></i>
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{ width: '100%', cursor: 'pointer' }}
                          onDoubleClick={() => handleDoubleClick(field.id, optionNode.value)}
                        >
                          {optionNode.label}
                        </div>
                      );
                    }}
                  />
                </Tooltip>
              </div>
              {helpText && <div className="semi-form-field-extra">{helpText}</div>}
              {errorMsg && <div className="semi-form-field-error-message">{errorMsg}</div>}
            </div>
          );
        } else {
          return (
            <Form.Select
              key={field.id}
              field={field.id}
              label={labelNode}
              optionList={options}
              placeholder={placeholder}
              rules={rules}
              allowCreate={false}
            />
          );
        }
      }
      case 'checkbox':
        return <Form.CheckboxGroup 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                options={options.map((opt: { label: string; value: string | number }) => opt as { label: string; value: string | number })} />;
      case 'radio':
        return <Form.RadioGroup 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                options={options.map((opt: { label: string; value: string | number }) => opt as { label: string; value: string | number })} />;
      case 'date':
        return <Form.DatePicker 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                placeholder={placeholder} />;
      case 'password':
        return <Form.Input 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                type="password" 
                placeholder={placeholder} 
                required={required} 
                rules={rules} />;
      case 'switch':
        return <Form.Switch 
                key={field.id}
                field={field.id} 
                label={labelNode} />;
      case 'slider':
        const currentValue = sliderValues[field.id] !== undefined ? sliderValues[field.id] : initialValues[field.id];
        const min = field.min !== undefined ? field.min : 0;
        const max = field.max !== undefined ? field.max : 100;
        const step = field.step !== undefined ? field.step : 1;
        const unit = field.unit || '';
        const percentage = ((currentValue - min) / (max - min)) * 100;
        return (
          <div key={field.id} style={{ width: '100%', position: 'relative' }}>
            <Form.Slider
              field={field.id}
              label={labelNode}
              min={min}
              max={max}
              step={step}
              marks={{
                [min]: `${min}${unit}`,
                [max]: `${max}${unit}`
              }}
              tipFormatter={(value) => `${value}${unit}`}
              style={{ width: '100%' }}
              onChange={(value) => {
                if (typeof value === 'number') {
                  setSliderValues(prev => ({
                    ...prev,
                    [field.id]: value
                  }));
                }
              }}
            />
            <div style={{
              position: 'absolute',
              top: '35%',
              left: `${Math.max(10, Math.min(90, percentage))}%`,
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#1890ff',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              zIndex: 10,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              {currentValue}{unit}
            </div>
          </div>
        );
      default:
        logger.warn("unkown form field type: ", field.type)
        return <Form.Input 
                key={field.id} 
                field={field.id} 
                label={labelNode} 
                placeholder={placeholder} 
                required={required} />;
    }
  };

  return (
    <Card>
      {props.form.title && (
        <>
          <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8, textAlign: 'center' }}>{props.form.title}</div>
          <div style={{ borderBottom: '1px solid #e5e6eb', margin: '0 auto 16px auto', width: '60%' }} />
        </>
      )}
      <Form
        ref={formRef}
        initValues={initialValues}
        onSubmit={handleSubmit}
        style={{ width: '100%' }}
      >
        {props.form.fields.map(renderField)}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28, marginBottom: 8 }}>
          <Button
            htmlType="submit"
            type="primary"
            size="large"
            style={{
              minWidth: 160,
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: '0 2px 12px rgba(25, 118, 210, 0.12)',
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.15)'
            }}
          >
            {props.form.submit_text ? t(props.form.submit_text) : t('pages.chat.submit')}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default NormalFormUI; 